import {
  BrainIcon,
  HandPalmIcon,
  RobotIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from 'src/lib/utils';
import { specificActionIcon } from 'src/components/common/agentActionFormatting';
import type { SpanType, SpanStatus } from './types';

export const TYPE_LABEL: Record<SpanType, string> = {
  llm: 'LLM',
  tool: 'Tool',
};

export function SpanTypeIcon({
  type,
  hasChildren = false,
  isAgent = false,
  isHuman = false,
  action,
}: {
  type: SpanType;
  /** A tool span with nested sub-steps is a subagent → robot icon. */
  hasChildren?: boolean;
  /** An A2A sub-agent (identity from an agent card) → accented robot icon. */
  isAgent?: boolean;
  /** A person intervening in the run (human takeover) → raised hand, tinted apart from the
   * agent's own glyphs so the eye can find where a human stepped in. */
  isHuman?: boolean;
  /**
   * The raw action of a leaf tool span. When it's one the operator thread has a glyph for, use
   * that glyph — a page of desktop work reads as clicks and keystrokes rather than as an
   * undifferentiated column of wrenches. Unrecognized actions keep the wrench.
   */
  action?: string | null;
}) {
  if (isHuman)
    return (
      <HandPalmIcon
        weight="fill"
        className="h-3.5 w-3.5 shrink-0 text-amber-700"
      />
    );
  if (isAgent)
    return <RobotIcon className="h-3.5 w-3.5 shrink-0 text-indigo-600" />;
  if (type === 'tool' && hasChildren)
    return <RobotIcon className="h-3.5 w-3.5 shrink-0 text-stone-600" />;
  if (type === 'tool') {
    const ActionIcon = action ? specificActionIcon(action) : null;
    if (ActionIcon)
      return (
        <ActionIcon
          weight="bold"
          className="h-3.5 w-3.5 shrink-0 text-stone-600"
        />
      );
    return <WrenchIcon className="h-3.5 w-3.5 shrink-0 text-stone-600" />;
  }
  return <BrainIcon className="h-3.5 w-3.5 shrink-0 text-stone-600" />;
}

export function StatusDot({ status }: { status: SpanStatus }) {
  return (
    <span
      className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', {
        'bg-green-600': status === 'completed',
        // Pulses so an in-progress span reads as active, not just amber.
        'bg-amber-500 animate-pulse': status === 'running',
        'bg-red-600': status === 'failed',
      })}
    />
  );
}

/**
 * "Live" affordance for a still-running trace: a pulsing dot plus the poll
 * cadence, so it's obvious the view refreshes itself rather than showing a
 * frozen snapshot. `isFetching` brightens it on each in-flight poll.
 */
export function LivePill({
  intervalMs,
  isFetching = false,
}: {
  intervalMs: number;
  isFetching?: boolean;
}) {
  const gt = useGT();
  const seconds = Math.max(1, Math.round(intervalMs / 1000));
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 h-6 px-2 rounded-full border text-[10.5px] font-semibold uppercase tracking-wider transition-colors',
        isFetching
          ? 'border-emerald-300 bg-emerald-100 text-emerald-800'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700',
      )}
      title={gt('This session is still running — refreshing every {seconds}s', {
        context: 'tooltip explaining the live-polling badge on an agent trace',
        seconds: String(seconds),
      })}
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-600" />
      </span>
      {gt('Live', {
        context:
          'badge label: agent trace is live-polling while the run is active',
      })}
      <span className="font-mono font-normal normal-case tracking-normal text-emerald-700/80">
        {gt('{seconds}s', {
          context: 'agent trace live badge poll interval, e.g. "3s"',
          seconds: String(seconds),
        })}
      </span>
    </span>
  );
}

export function FilterChip({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[11.5px] font-medium cursor-pointer transition-all duration-100',
        active
          ? 'border-stone-700 bg-stone-800 text-white'
          : 'border-border bg-white text-stone-700 hover:bg-stone-50',
      )}
    >
      {children}
      {count != null && (
        <span
          className={cn(
            'text-[10px] font-semibold px-1.5 rounded-full tabular-nums',
            active
              ? 'bg-white/20 text-white/90'
              : 'bg-stone-100 text-muted-foreground',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function StatPill({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 px-3.5 border-r border-border min-w-0 flex-1">
      <div className="text-[10px] font-semibold tracking-widest uppercase text-stone-500 truncate">
        {label}
      </div>
      <div
        className="text-[15px] font-bold font-mono tabular-nums tracking-tight truncate"
        style={{ color: accent ?? 'hsl(var(--foreground))' }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[10.5px] text-muted-foreground truncate">
          {sub}
        </div>
      )}
    </div>
  );
}
